/**
 * audioStorage.js — Firebase Storage utility for voice capture uploads.
 *
 * Stores audio at: users/{uid}/voice/{timestamp}_{uuid}.webm
 * Returns a public download URL to store as a reference instead of base64.
 *
 * Why this exists: Firestore documents have a 1 MiB limit, so embedding
 * base64 audio directly in an inbox item is a latent write failure on any
 * longer recording. Uploading to Cloud Storage and keeping only the download
 * URL in the item sidesteps that entirely, and is what makes a recording
 * genuinely recoverable/replayable later (the URL feeds straight into
 * CustomAudioPlayer's `src` prop — see src/components/ui/CustomAudioPlayer.jsx).
 */
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, auth } from '../firebase/config';

/**
 * Mirrors storage.rules' `users/{userId}/voice/` size cap exactly — keep
 * both in sync. Validating here BEFORE attempting the upload means an
 * oversized recording fails fast with a clear, catchable reason instead of
 * a wasted round trip that Storage's rules would reject anyway.
 */
export const MAX_VOICE_AUDIO_BYTES = 10 * 1024 * 1024;

function typedError(message, code) {
    const err = new Error(message);
    err.code = code;
    return err;
}

/** Strips an optional `data:...;base64,` prefix, returning the raw base64 body. */
function stripDataUrlPrefix(base64Data) {
    const commaIdx = base64Data.indexOf(',');
    return commaIdx >= 0 ? base64Data.slice(commaIdx + 1) : base64Data;
}

/** Decoded byte length of a base64 (data-URL or bare) string, without allocating the bytes. */
function base64ByteLength(base64Data) {
    const raw = stripDataUrlPrefix(base64Data);
    const padding = raw.endsWith('==') ? 2 : raw.endsWith('=') ? 1 : 0;
    return Math.max(0, Math.floor((raw.length * 3) / 4) - padding);
}

function base64ToBytes(base64Data) {
    const raw = stripDataUrlPrefix(base64Data);
    const binaryStr = atob(raw);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
    }
    return bytes;
}

/**
 * Upload voice capture audio to Firebase Storage.
 * Accepts either a Blob/File (preferred — straight from MediaRecorder, no
 * base64 round trip needed) or a base64-encoded data URL/string, for
 * backward compatibility with existing callers that already produce one.
 *
 * @param {Blob|string} audioData - a Blob, or base64 data URL / bare base64 string
 * @param {string} mimeType - MIME type of the audio (default: 'audio/webm')
 * @param {string} [captureId] - a stable id for THIS recording (e.g. the
 *   captureId useVoiceDumpStore.js mints per take), used as the object's
 *   filename instead of a fresh random one. Passing the SAME captureId on
 *   every retry of the same recording (including one resumed on a later
 *   launch — see savePendingCapture()/listPendingCaptures() below) makes
 *   retries idempotent: a retry overwrites the one object for this capture
 *   instead of creating a new one, so a response lost after a successful
 *   upload can never leave a duplicate, unreferenced object behind. Callers
 *   that don't have one (legacy callers, one-shot uploads) fall back to the
 *   original random name.
 * @returns {Promise<string>} The download URL to store as a reference
 * @throws {Error} .code === 'NOT_AUTHENTICATED' | 'AUDIO_TOO_LARGE'
 */
export async function uploadVoiceCapture(audioData, mimeType = 'audio/webm', captureId) {
    const uid = auth.currentUser?.uid;
    if (!uid) throw typedError('User not authenticated', 'NOT_AUTHENTICATED');

    const isBlob = typeof Blob !== 'undefined' && audioData instanceof Blob;
    const size = isBlob ? audioData.size : base64ByteLength(audioData);
    if (size > MAX_VOICE_AUDIO_BYTES) {
        const mb = (n) => (n / (1024 * 1024)).toFixed(1);
        throw typedError(
            `Recording is ${mb(size)}MB — over the ${mb(MAX_VOICE_AUDIO_BYTES)}MB limit for saved voice captures.`,
            'AUDIO_TOO_LARGE'
        );
    }

    const filename = captureId
        ? `capture_${captureId}.webm`
        : `${Date.now()}_${crypto.randomUUID().slice(0, 8)}.webm`;
    const storageRef = ref(storage, `users/${uid}/voice/${filename}`);
    const payload = isBlob ? audioData : base64ToBytes(audioData);

    await uploadBytes(storageRef, payload, { contentType: mimeType });
    return getDownloadURL(storageRef);
}

/**
 * Delete a previously-uploaded voice capture, given the download URL (or a
 * bucket-relative path) stored in an inbox attachment's `data` field. Best
 * effort and never throws: an inline `data:` URL (no Storage object behind
 * it), an object that's already gone, or any other failure is swallowed —
 * this is cleanup, not the user-facing action, and AGENTS.md's guilt-free
 * rule means a cleanup hiccup must never surface as a scary error.
 *
 * @param {string} urlOrPath
 * @returns {Promise<boolean>} true if a Storage object was actually deleted
 */
export async function deleteVoiceCapture(urlOrPath) {
    if (!urlOrPath || typeof urlOrPath !== 'string' || urlOrPath.startsWith('data:')) {
        return false; // inline/legacy base64 attachment — no Storage object to remove
    }
    try {
        await deleteObject(ref(storage, urlOrPath));
        return true;
    } catch (err) {
        if (err?.code !== 'storage/object-not-found') {
            console.warn('[audioStorage] Could not delete voice capture object:', err?.message || err);
        }
        return false;
    }
}

/**
 * ─── Durable local copy of an in-flight capture ───
 *
 * Until uploadVoiceCapture() resolves, a recording's ONLY copy is the
 * in-memory Blob passed to it — if the tab or app closes while that upload
 * (or a later automatic retry) is still running, the in-memory blob dies
 * with it and the recording is gone for good. IndexedDB is the only
 * browser-native store built for arbitrary-size binary data that survives a
 * reload, so useVoiceDumpStore.js writes the blob here BEFORE attempting the
 * upload and only removes it once the outcome is final (uploaded, or
 * explicitly discarded) — see its persistCapture()/recoverPendingCaptures().
 *
 * Every function below is best-effort and never throws: this is a safety
 * net for a safety net. IndexedDB can be unavailable (very old browser,
 * private-browsing in some engines, site data disabled) or fail (quota) for
 * reasons that must never block the recording's PRIMARY path — the direct
 * upload — which works with or without any of this.
 */
const PENDING_CAPTURE_DB_NAME = 'alphafocus-voice-capture';
const PENDING_CAPTURE_DB_VERSION = 1;
const PENDING_CAPTURE_STORE = 'pending_uploads';

function openPendingCaptureDB() {
    return new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('IndexedDB is not available in this environment'));
            return;
        }
        const request = indexedDB.open(PENDING_CAPTURE_DB_NAME, PENDING_CAPTURE_DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(PENDING_CAPTURE_STORE)) {
                db.createObjectStore(PENDING_CAPTURE_STORE, { keyPath: 'id' });
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error || new Error('Failed to open the local capture database'));
    });
}

/**
 * Persists a capture's raw blob locally, keyed by captureId, so it survives
 * a reload before its upload is confirmed. Never throws.
 */
export async function savePendingCapture(captureId, audioBlob, mimeType) {
    try {
        const db = await openPendingCaptureDB();
        await new Promise((resolve, reject) => {
            const tx = db.transaction(PENDING_CAPTURE_STORE, 'readwrite');
            tx.objectStore(PENDING_CAPTURE_STORE).put({ id: captureId, blob: audioBlob, mimeType, savedAt: Date.now() });
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
    } catch (err) {
        console.warn('[audioStorage] Could not save a durable local copy of this recording:', err?.message || err);
    }
}

/**
 * Removes a captureId's durable local copy once its upload is confirmed (or
 * it has been explicitly discarded — e.g. a re-record). Never throws: worst
 * case a harmless local row lingers and is retried again next launch.
 */
export async function deletePendingCapture(captureId) {
    try {
        const db = await openPendingCaptureDB();
        await new Promise((resolve, reject) => {
            const tx = db.transaction(PENDING_CAPTURE_STORE, 'readwrite');
            tx.objectStore(PENDING_CAPTURE_STORE).delete(captureId);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
    } catch {
        // Best effort — see the section docblock above.
    }
}

/**
 * Lists every capture whose upload never confirmed — survivors of a tab/app
 * close mid-upload (or mid-retry). Returns [] on any failure (including no
 * IndexedDB support at all) rather than throwing, so a corrupt or
 * unavailable local database can never block app startup.
 *
 * @returns {Promise<Array<{id: string, blob: Blob, mimeType: string, savedAt: number}>>}
 */
export async function listPendingCaptures() {
    try {
        const db = await openPendingCaptureDB();
        return await new Promise((resolve, reject) => {
            const tx = db.transaction(PENDING_CAPTURE_STORE, 'readonly');
            const request = tx.objectStore(PENDING_CAPTURE_STORE).getAll();
            request.onsuccess = () => resolve(request.result || []);
            request.onerror = () => reject(request.error);
        });
    } catch {
        return [];
    }
}
